/**
 * The stories, read the way the native apps read them: the manifest and each
 * scene's pack from the content server (cdn.tapvocab.app/packs/v1/, see
 * docs/native-packs.md). Paths in them are relative to the manifest.
 */

export type Voice = "f" | "m";

export interface Language {
  code: string;
  label: string;
  locale: string;
  direction?: "ltr" | "rtl";
  /** a language being tried out in a few stories: only these offer it */
  stories?: string[];
}

export interface SceneEntry {
  slug: string;
  label: string;
  title: string;
  alt: string;
  image: string;
  width: number;
  height: number;
  itemCount: number;
  pack: string;
  version: string;
  words?: string[];
}

export interface Story {
  slug: string;
  title: string;
  cover?: string;
  scenes: SceneEntry[];
}

export interface Manifest {
  languages: Language[];
  scenes: SceneEntry[];
  stories?: Story[];
  phrases?: { tryAgain?: Record<string, Partial<Record<Voice, string>>> };
}

export interface Translation {
  text: string;
  phonetic: string;
  /** the word in kana, for Japanese written in kanji */
  reading?: string;
  audio?: string;
  voices?: Partial<Record<Voice, string>>;
}

export type Ring = [number, number][];

export interface Item {
  id: string;
  english: string;
  polygons: Ring[];
  translations: Record<string, Translation>;
}

export interface Pack {
  slug: string;
  label: string;
  title: string;
  alt: string;
  image: string;
  width: number;
  height: number;
  items: Item[];
  /** the tall picture for a phone held upright, with each item's outline in it */
  portrait?: { image: string; width: number; height: number; polygons: Record<string, Ring[]> };
}

/** One picture of a scene and the outlines in it: the wide one, or the tall one. */
export interface Picture {
  image: string;
  width: number;
  height: number;
  /** each item's outline in this picture; empty where it isn't drawn */
  outlines: Map<string, Ring[]>;
}

export const MANIFEST_URL: string =
  import.meta.env.VITE_MANIFEST_URL ?? "https://cdn.tapvocab.app/packs/v1/manifest.json";

export const url = (path: string) => new URL(path, new URL(MANIFEST_URL, location.href)).href;

let manifest: Promise<Manifest> | null = null;
export function loadManifest(): Promise<Manifest> {
  // no-cache on the server: the browser asks whether it changed, so a new story shows up on the next visit
  manifest ??= fetch(url("manifest.json")).then((r) => {
    if (!r.ok) throw new Error(`manifest: ${r.status}`);
    return r.json();
  });
  return manifest;
}

const packs = new Map<string, Promise<Pack>>();
export function loadPack(scene: SceneEntry): Promise<Pack> {
  // the version in the address, so a changed scene is fetched fresh and an unchanged one comes from the cache
  const key = `${scene.pack}?v=${scene.version}`;
  let pack = packs.get(key);
  if (!pack) {
    pack = fetch(url(key)).then((r) => {
      if (!r.ok) throw new Error(`${scene.slug}: ${r.status}`);
      return r.json();
    });
    packs.set(key, pack);
  }
  return pack;
}

export function widePicture(pack: Pack): Picture {
  return { image: pack.image, width: pack.width, height: pack.height, outlines: new Map(pack.items.map((i) => [i.id, i.polygons])) };
}

export function tallPicture(pack: Pack): Picture | null {
  const p = pack.portrait;
  if (!p) return null;
  return { image: p.image, width: p.width, height: p.height, outlines: new Map(pack.items.map((i) => [i.id, p.polygons[i.id] ?? []])) };
}

/** One entry per English word, in pack order: the vocabulary list. */
export function vocabulary(items: Item[]): Item[] {
  const seen = new Set<string>();
  return items.filter((i) => !seen.has(i.english) && seen.add(i.english));
}

/** The recording to play: the chosen voice, else the other, else the single older one. */
export function recording(t: Translation | undefined, voice: Voice): string | null {
  if (!t) return null;
  const path = t.voices?.[voice] ?? t.voices?.[voice === "f" ? "m" : "f"] ?? t.audio;
  return path ? url(path) : null;
}

/** The languages a story offers: a language with a whitelist only in its stories. */
export function languagesFor(m: Manifest, story: string | null): Language[] {
  return m.languages.filter((l) => !l.stories || (story !== null && l.stories.includes(story)));
}

export const pickerLabel = (l: Language) => (l.stories ? `${l.label} (preview)` : l.label);

// ---- the learner's choices, kept in this browser

function read<T>(key: string, fallback: T): T {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : (JSON.parse(v) as T);
  } catch {
    return fallback;
  }
}
function write(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private window: the choice lasts this visit only */
  }
}

export const prefs = {
  language: () => read<string | null>("tv.language", null),
  setLanguage: (code: string) => write("tv.language", code),
  voice: () => read<Voice>("tv.voice", "f"),
  setVoice: (v: Voice) => write("tv.voice", v),
  explored: (scene: string) => new Set(read<string[]>(`tv.explored.${scene}`, [])),
  setExplored: (scene: string, words: Set<string>) => write(`tv.explored.${scene}`, [...words]),
};

/** The language last chosen, else the first one every story has (Spanish). */
export function defaultLanguage(m: Manifest): string {
  const saved = prefs.language();
  if (saved && m.languages.some((l) => l.code === saved)) return saved;
  return m.languages.find((l) => !l.stories)?.code ?? m.languages[0].code;
}
